import { useEffect, useMemo, useRef, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Box, Button, Container, Divider } from '@mantine/core'
import { Image } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useReactToPrint } from 'react-to-print'

dayjs.extend(utc)
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import { useMutation, useQuery } from '@redwoodjs/web'

import { PDFViewer } from 'src/components/DataDisplay/PDFViewer/PDFViewer'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  GET_DOCUMENT_DOWNLOAD_URL,
  GET_DOCUMENT_INTELLIGENCE,
  GET_DOCUMENT_VOE_LINK,
  REVIEW_DOCUMENT_PREDICTIONS,
  UPDATE_DOCUMENT,
} from 'src/graphql/documents.graphql'
import { FileContentType } from 'src/graphql/types/documents'
import { DocumentUserStatus } from 'src/graphql/types/documents'
import useVoEDocumentCompletionGate from 'src/hooks/useVoEDocumentCompletionGate/useVoEDocumentCompletionGate'
import IconSparkle from 'src/icons/IconSparkle'
import { formatDocumentCategory } from 'src/lib/formatters'
import { cn } from 'src/utils'
import { isSameUtcDay } from 'src/utils/date/toMonthYearKey'
import { downloadFile } from 'src/utils/downloadFile'

// Side-panel card for an AI prediction awaiting recruiter confirmation.
const AiActionCard = ({
  prediction,
  onApply,
  onDismiss,
}: {
  prediction: any
  onApply: () => void
  onDismiss: () => void
}) => {
  const isExpiration = prediction.field === 'DATE'
  const valueLabel = isExpiration
    ? dayjs.utc(prediction.expirationValue).format('MM/DD/YYYY')
    : formatDocumentCategory(prediction.categoryValue)
  const fieldLabel = isExpiration ? 'Expiration Date' : 'Category'
  const pct = Math.round(prediction.confidence * 100)

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs">
      <div className="flex items-center justify-between gap-1 font-semibold text-blue-700">
        <div className="flex items-center gap-1">
          <IconSparkle size={11} />
          AI predicted
        </div>
        <span className="font-normal text-blue-500">{pct}%</span>
      </div>
      <div className="text-doubleNickel-gray-600">
        <span className="font-medium">{fieldLabel}:</span>{' '}
        <span className="font-semibold text-doubleNickel-gray-800">
          {valueLabel}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        <Button
          size="xs"
          variant="filled"
          onClick={onApply}
          fullWidth
          styles={{ root: { backgroundColor: '#1C93D7', border: 'none' } }}
        >
          Apply {valueLabel}
        </Button>
        <Button
          size="xs"
          variant="outline"
          onClick={onDismiss}
          fullWidth
          styles={{ root: { borderColor: '#93C5FD', color: '#1E40AF' } }}
        >
          Dismiss
        </Button>
      </div>
    </div>
  )
}

import DocumentEditScreen from './DocumentEditScreen/DocumentEditScreen'
import DocumentPreviewHeader from './DocumentPreviewHeader'
import type { DocumentNavigation } from './DocumentPreviewHeader/DocumentPreviewHeader'
import ZoomControls from './ZoomControls/ZoomControls'

const imageTypes = [
  FileContentType.JPG,
  FileContentType.PNG,
  FileContentType.GIF,
  FileContentType.TIF,
  FileContentType.HEIC,
]

const supportedPreviewTypes = [...imageTypes, FileContentType.PDF]

const DocumentPreview = ({
  applicantId,
  documentId,
  showBackButton = true,
  showClosebutton = false,
  onBack,
  showActions = true,
  refetchQueries = [],
  navigation = undefined,
}: {
  applicantId: string
  documentId: string
  showBackButton?: boolean
  showClosebutton?: boolean
  // Passed by AttachmentsMenu; not read by the preview itself
  showStatus?: boolean
  onBack: () => void
  showActions?: boolean
  refetchQueries?: any[]
  // When set, the header steps through a list of documents and Approve moves
  // on to the next one instead of leaving the preview.
  navigation?: DocumentNavigation
}) => {
  const currentCompany = useCurrentCompanyContext()
  const showAi = currentCompany?.documentIntelligenceEnabled ?? false

  // The latest navigation, read when an update finishes (the list can finish
  // loading while the update is in flight)
  const navigationRef = useRef(navigation)
  navigationRef.current = navigation

  const componentRef = useRef()
  const [zoomedIn, setZoomedIn] = useState(false)
  const [documentRotation, setDocumentRotation] = useState(0)
  // Fields hidden from the side panel (union of applied + skipped)
  const [dismissedFields, setDismissedFields] = useState<Set<string>>(new Set())
  // Subset of dismissedFields where the user clicked "Apply" (value was written to document)
  const [appliedFields, setAppliedFields] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (documentId) {
      getDocumentUrlForPreview({
        variables: {
          id: documentId,
          applicantId: applicantId,
        },
      })
    }
    // Reset per-document AI review state whenever we switch to a different document
    setDismissedFields(new Set())
    setAppliedFields(new Set())
  }, [documentId])

  // Completing a document can complete its linked verification, so the link
  // is refetched with every update.
  const allRefetchQueries = [
    ...refetchQueries,
    GET_DOCUMENT_DOWNLOAD_URL,
    GET_DOCUMENT_VOE_LINK,
  ]

  // The verification this document is linked to: marking it Completed while
  // that verification is open asks how the verification was completed.
  const skipVoELink = !documentId || !applicantId
  const {
    data: voeLinkData,
    loading: voeLinkLoading,
    error: voeLinkError,
    refetch: refetchVoELink,
  } = useQuery(GET_DOCUMENT_VOE_LINK, {
    variables: { id: documentId, applicantId },
    skip: skipVoELink,
  })
  const linkedVoE = voeLinkData?.document?.voe ?? null
  // Until the link is known, a Completed change can't tell whether it also
  // completes a verification, so it waits (and a failed lookup is retried).
  const voeLinkPending =
    !skipVoELink && (voeLinkLoading || !!voeLinkError || !voeLinkData)
  const retryVoELink = () => {
    if (voeLinkError) refetchVoELink()
  }

  // Fetch document intelligence separately (not part of DownloadUrlResult)
  const { data: intelligenceData } = useQuery(GET_DOCUMENT_INTELLIGENCE, {
    variables: { id: documentId, applicantId },
    skip: !documentId || !showAi,
    fetchPolicy: 'cache-and-network',
  })
  const documentIntelligence =
    intelligenceData?.document?.documentIntelligence ?? null

  const [
    getDocumentUrlForPreview,
    { data: documentForPreviewResponse, loading: loadingPreview },
  ] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_URL, {
    onError: () => {
      toast(
        'Unable to get document url for preview, please try again.',
        'error'
      )
    },
  })

  // Derived from the query data (not state set in onCompleted) so that
  // refetches triggered by UPDATE_DOCUMENT flow straight through to the
  // metadata strip — onCompleted does not re-fire on refetch, which left
  // category/status/checklist edits showing stale values.
  const downloadedDocument = documentForPreviewResponse?.documentDownloadUrl
    ?.url
    ? documentForPreviewResponse.documentDownloadUrl
    : null

  // Low-confidence predictions that need recruiter review (not yet dismissed/accepted).
  // Date comparisons use isSameUtcDay so this filter agrees with
  // DocumentEditScreen's getChipState — otherwise a prediction can render as
  // "accepted" in the chip while still appearing as a low-confidence action card.
  const lowConfidencePredictions = useMemo(() => {
    if (!documentIntelligence || documentIntelligence.status !== 'COMPLETED')
      return []
    return documentIntelligence.predictions.filter((p) => {
      if (p.autoApplied) return false
      if (p.field !== 'DATE' && p.field !== 'CATEGORY') return false
      if (dismissedFields.has(p.field)) return false
      if (!p.expirationValue && !p.categoryValue) return false
      // Already reviewed in a previous session — don't re-surface the card.
      if (
        p.reviewDecision === 'KEPT_ORIGINAL' ||
        p.reviewDecision === 'ACCEPTED_AI'
      )
        return false
      // Hide the card if the document's value already matches the suggestion —
      // this means the user accepted it in a previous session (autoApplied stays
      // false for manual accepts, so we must infer from the value).
      if (
        p.field === 'DATE' &&
        p.expirationValue &&
        downloadedDocument?.expirationDate
      ) {
        if (isSameUtcDay(p.expirationValue, downloadedDocument.expirationDate))
          return false
      }
      if (
        p.field === 'CATEGORY' &&
        p.categoryValue &&
        downloadedDocument?.category
      ) {
        if (p.categoryValue === downloadedDocument.category) return false
      }
      return true
    })
  }, [documentIntelligence, dismissedFields, downloadedDocument])

  // Always refetch GET_DOCUMENT_INTELLIGENCE on update — the backend flips
  // DocumentPrediction.overriddenAfterAutoApply when a recruiter edits a field
  // that AI had auto-applied. Without this refetch, Apollo serves stale
  // predictions and the chip-hiding guard in getChipState never fires.
  const [updateDocument] = useMutation(UPDATE_DOCUMENT, {
    refetchQueries: [...allRefetchQueries, GET_DOCUMENT_INTELLIGENCE],
    onCompleted: () => {
      toast('Document updated successfully', 'success')
    },
    onError: (error) => {
      toast(
        error?.message || 'Unable to update document, please try again.',
        'error'
      )
    },
  })

  const [reviewDocumentPredictions] = useMutation(REVIEW_DOCUMENT_PREDICTIONS, {
    refetchQueries: [GET_DOCUMENT_INTELLIGENCE],
  })

  const onPrint = () => {
    if (downloadedDocument.fileType === 'PDF') {
      onPrintPDF()
    } else if (imageTypes.includes(downloadedDocument.fileType)) {
      onPrintImage()
    } else {
      toast('Print is not available for this format.', 'warning')
    }
  }

  const onPrintImage = useReactToPrint({
    content: () => componentRef.current,
  })

  const onPrintPDF = () => {
    window.open(downloadedDocument.url, 'PRINT')
  }

  const handleDownload = () => {
    const { url, fileName, fileType } = downloadedDocument
    downloadFile({ url, fileName, fileType })
  }

  const { requestStatusChange, modal: voeCompletionModal } =
    useVoEDocumentCompletionGate()

  const handleApprove = () => {
    const userStatus =
      downloadedDocument.userStatus === DocumentUserStatus.PENDING_REVIEW
        ? DocumentUserStatus.COMPLETED
        : DocumentUserStatus.PENDING_REVIEW
    requestStatusChange({
      voe: linkedVoE,
      userStatus,
      currentStatus: downloadedDocument.userStatus,
      voeLinkPending,
      onVoELinkPending: retryVoELink,
      send: async (voeCompletion) => {
        const result = await updateDocument({
          variables: {
            id: documentId,
            input: {
              userStatus,
              ...(voeCompletion && { voeCompletion }),
            },
          },
        })
        // Stay on the preview when the update was refused, so the recruiter
        // sees the error and the unchanged document
        if (result?.data?.updateDocument) {
          // Keep reviewing: advance to the next document, and only leave the
          // preview after the last one (or when there is no list to step
          // through). While the list is still loading we can't tell, so stay.
          const nav = navigationRef.current
          if (nav?.loading) return
          if (nav?.onNext) nav.onNext()
          else onBack()
        }
      },
    })
  }

  const applyPrediction = (prediction: any) => {
    const input: Record<string, unknown> = {}
    if (prediction.field === 'DATE' && prediction.expirationValue) {
      input.expirationDate = new Date(prediction.expirationValue)
    } else if (prediction.field === 'CATEGORY' && prediction.categoryValue) {
      input.category = prediction.categoryValue
    }
    if (Object.keys(input).length > 0) {
      updateDocument({ variables: { id: documentId, input } })
    }
    reviewDocumentPredictions({
      variables: {
        documentId,
        decisions: [{ field: prediction.field, decision: 'ACCEPTED_AI' }],
      },
    })
    setDismissedFields((prev) => new Set([...prev, prediction.field]))
    setAppliedFields((prev) => new Set([...prev, prediction.field]))
  }

  const dismissPrediction = (field: string) => {
    reviewDocumentPredictions({
      variables: {
        documentId,
        decisions: [{ field, decision: 'KEPT_ORIGINAL' }],
      },
    })
    setDismissedFields((prev) => new Set([...prev, field]))
  }

  if (!downloadedDocument) return null
  return (
    <div className="relative flex flex-1 flex-col gap-3 overflow-hidden">
      <DocumentPreviewHeader
        fileName={downloadedDocument.fileName}
        documentId={documentId}
        onRender={allRefetchQueries}
        showActions={
          showActions &&
          downloadedDocument.userStatus == DocumentUserStatus.PENDING_REVIEW
        }
        showBackButton={showBackButton}
        showClosebutton={showClosebutton}
        onBack={() => onBack()}
        onPrint={onPrint}
        onDownload={handleDownload}
        onApprove={handleApprove}
        navigation={navigation}
      />
      <DocumentEditScreen
        document={downloadedDocument}
        documentId={documentId}
        applicantId={applicantId}
        onRender={allRefetchQueries}
        documentIntelligence={showAi ? documentIntelligence : null}
        appliedFields={appliedFields}
        dismissedFields={dismissedFields}
        voe={linkedVoE}
        voeLinkPending={voeLinkPending}
        onVoELinkPending={retryVoELink}
      />
      {voeCompletionModal}
      <Divider />
      <div className="flex min-h-0 flex-1 gap-3 overflow-hidden">
        <Container fluid className="w-full flex-1 overflow-auto">
          {downloadedDocument.fileType === 'PDF' && (
            <PDFViewer url={downloadedDocument.url} />
          )}
          {imageTypes.includes(downloadedDocument.fileType) && (
            <TransformWrapper
              limitToBounds={false}
              centerZoomedOut
              wheel={{
                wheelDisabled: true,
                disabled: true,
              }}
              panning={{
                wheelPanning: zoomedIn,
              }}
            >
              {({ zoomIn, resetTransform }) => (
                <Box className="relative">
                  <div className="sticky top-0 z-50">
                    <ZoomControls
                      zoomIn={() => {
                        setZoomedIn(true)
                        zoomIn()
                      }}
                      zoomOut={() => {
                        resetTransform()
                        setZoomedIn(false)
                      }}
                      setDocumentRotation={setDocumentRotation}
                    />
                  </div>
                  <TransformComponent>
                    <div
                      ref={componentRef}
                      className="flex items-center justify-center"
                    >
                      <Image
                        src={downloadedDocument.url}
                        alt={downloadedDocument.fileName}
                        h="auto"
                        w="auto"
                        fit="contain"
                        className={cn(
                          documentRotation === 90 && 'rotate-90',
                          documentRotation === 180 && 'rotate-180',
                          documentRotation === 270 && '-rotate-90'
                        )}
                      />
                    </div>
                  </TransformComponent>
                </Box>
              )}
            </TransformWrapper>
          )}

          {!supportedPreviewTypes.includes(downloadedDocument.fileType) && (
            <Box className="flex h-96 items-center justify-center">
              <p className="text-gray-500">
                File preview is not available for this format. Please download
                the file to view its contents.
              </p>
            </Box>
          )}
        </Container>

        {/* AI side panel: low-confidence predictions that need review */}
        {lowConfidencePredictions.length > 0 && (
          <div className="flex w-52 shrink-0 flex-col gap-2 overflow-y-auto py-1 pr-1">
            {lowConfidencePredictions.map((prediction) => (
              <AiActionCard
                key={prediction.field}
                prediction={prediction}
                onApply={() => applyPrediction(prediction)}
                onDismiss={() => dismissPrediction(prediction.field)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DocumentPreview
